
/**
 * Zodiac Message Management Service
 * 
 * This service handles all API calls related to zodiac message management
 * including CRUD operations, bulk updates, and personalized message generation.
 * 
 * Features:
 * - Create new zodiac message configurations
 * - Update existing zodiac message templates
 * - List all zodiac messages with pagination
 * - Delete zodiac message configurations
 * - Bulk update multiple zodiac messages
 * - Generate personalized messages for testing
 * 
 * @author Admin Team
 * @version 1.0.0
 */
import {
    instance as axios,
    setJwtToken,
    setMultiPartHeader,
  } from "../../config/axiosInstance";
  
/**
 * Create a new zodiac message configuration
 * 
 * @param {Object} data - Zodiac message data
 * @param {string} data.zodiacSign - Zodiac sign (aries, taurus, etc.)
 * @param {string} data.displayName - Display name for the zodiac sign
 * @param {string} data.messageTemplate - Message template with {{name}} placeholder
 * @param {string} [data.personalizationInstructions] - Instructions for personalization
 * @param {number} [data.wordLimit] - Word limit for the message (10-100)
 * @param {boolean} [data.isActive] - Whether the configuration is active
 * @returns {Promise<Object>} API response with created zodiac message
 */
export const createZodiacMessageApi = async (data) => {
    try {
        const response = await axios.post('/admin/zodiac-message/create', data, setJwtToken());
        return response.data;
    } catch (error) {
        console.error('Error creating zodiac message:', error);
        throw error;
    }
};

/**
 * Update an existing zodiac message configuration
 * 
 * @param {Object} data - Update data
 * @param {string} data.zodiacSign - Zodiac sign to update
 * @param {string} [data.displayName] - New display name
 * @param {string} [data.messageTemplate] - New message template
 * @param {string} [data.personalizationInstructions] - New personalization instructions
 * @param {number} [data.wordLimit] - New word limit
 * @param {boolean} [data.isActive] - New active status
 * @returns {Promise<Object>} API response with updated zodiac message
 */
export const updateZodiacMessageApi = async (data) => {
    try {
        const response = await axios.put('/admin/zodiac-message/update', data, setJwtToken());
        return response.data;
    } catch (error) {
        console.error('Error updating zodiac message:', error);
        throw error;
    }
};

/**
 * Get a specific zodiac message configuration by sign
 * 
 * @param {string} zodiacSign - Zodiac sign to fetch
 * @returns {Promise<Object>} API response with zodiac message configuration
 */
export const getZodiacMessageApi = async (zodiacSign) => {
    try {
        const response = await axios.get(`/admin/zodiac-message/get?zodiacSign=${zodiacSign}`, setJwtToken());
        return response.data;
    } catch (error) {
        console.error('Error fetching zodiac message:', error);
        throw error;
    }
};

/**
 * Get all zodiac message configurations with pagination and filtering
 * 
 * @param {Object} params - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=12] - Items per page
 * @param {string} [params.isActive] - Filter by active status ('true' or 'false')
 * @returns {Promise<Object>} API response with paginated zodiac messages
 */
export const getAllZodiacMessagesApi = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Add pagination parameters
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
        
        const response = await axios.get(`/admin/zodiac-message/list?${queryParams.toString()}`, setJwtToken());
        return response.data;
    } catch (error) {
        console.error('Error fetching zodiac messages list:', error);
        throw error;
    }
};

/**
 * Delete a zodiac message configuration (soft delete)
 * 
 * @param {string} zodiacSign - Zodiac sign to delete
 * @returns {Promise<Object>} API response with deletion result
 */
export const deleteZodiacMessageApi = async (zodiacSign) => {
    try {
        const response = await axios.delete('/admin/zodiac-message/delete', {
            data: { zodiacSign }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting zodiac message:', error);
        throw error;
    }
};

/**
 * Bulk update multiple zodiac message configurations
 * 
 * @param {Array} messages - Array of message update objects
 * @param {string} messages[].zodiacSign - Zodiac sign to update
 * @param {string} [messages[].displayName] - New display name
 * @param {string} [messages[].messageTemplate] - New message template
 * @param {string} [messages[].personalizationInstructions] - New personalization instructions
 * @param {number} [messages[].wordLimit] - New word limit
 * @param {boolean} [messages[].isActive] - New active status
 * @returns {Promise<Object>} API response with bulk update result
 */
export const bulkUpdateZodiacMessagesApi = async (messages) => {
    try {
        const response = await axios.post('/admin/zodiac-message/bulk-update', {
            messages
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk updating zodiac messages:', error);
        throw error;
    }
};

/**
 * Generate a personalized message for testing purposes
 * 
 * @param {string} zodiacSign - Zodiac sign
 * @param {string} [userName] - User name for personalization
 * @returns {Promise<Object>} API response with personalized message
 */
export const getPersonalizedMessageApi = async (zodiacSign, userName = 'Test User') => {
    try {
            const response = await axios.get(`/admin/zodiac-message/personalized?zodiacSign=${zodiacSign}&userName=${encodeURIComponent(userName)}`, setJwtToken());
        return response.data;
    } catch (error) {
        console.error('Error generating personalized message:', error);
        throw error;
    }
};

/**
 * Get all zodiac signs for dropdown/select options
 * 
 * @returns {Array} Array of zodiac sign objects with value and label
 */
export const getZodiacSignOptions = () => {
    return [
        { value: 'aries', label: 'Aries ♈', emoji: '♈' },
        { value: 'taurus', label: 'Taurus ♉', emoji: '♉' },
        { value: 'gemini', label: 'Gemini ♊', emoji: '♊' },
        { value: 'cancer', label: 'Cancer ♋', emoji: '♋' },
        { value: 'leo', label: 'Leo ♌', emoji: '♌' },
        { value: 'virgo', label: 'Virgo ♍', emoji: '♍' },
        { value: 'libra', label: 'Libra ♎', emoji: '♎' },
        { value: 'scorpio', label: 'Scorpio ♏', emoji: '♏' },
        { value: 'sagittarius', label: 'Sagittarius ♐', emoji: '♐' },
        { value: 'capricorn', label: 'Capricorn ♑', emoji: '♑' },
        { value: 'aquarius', label: 'Aquarius ♒', emoji: '♒' },
        { value: 'pisces', label: 'Pisces ♓', emoji: '♓' }
    ];
};

/**
 * Validate message template for proper {{name}} placeholder usage
 * 
 * @param {string} template - Message template to validate
 * @returns {Object} Validation result with isValid and message
 */
export const validateMessageTemplate = (template) => {
    if (!template || typeof template !== 'string') {
        return {
            isValid: false,
            message: 'Message template is required'
        };
    }

    if (template.length < 10) {
        return {
            isValid: false,
            message: 'Message template must be at least 10 characters long'
        };
    }

    if (template.length > 500) {
        return {
            isValid: false,
            message: 'Message template cannot exceed 500 characters'
        };
    }

    // Check if template contains {{name}} placeholder
    if (!template.includes('{{name}}')) {
        return {
            isValid: false,
            message: 'Message template must contain {{name}} placeholder for personalization'
        };
    }

    // Check for multiple {{name}} placeholders (should be only one)
    const namePlaceholderCount = (template.match(/\{\{name\}\}/g) || []).length;
    if (namePlaceholderCount > 1) {
        return {
            isValid: false,
            message: 'Message template should contain only one {{name}} placeholder'
        };
    }

    return {
        isValid: true,
        message: 'Message template is valid'
    };
};

/**
 * Generate preview of personalized message
 * 
 * @param {string} template - Message template
 * @param {string} userName - User name for preview
 * @returns {string} Personalized message preview
 */
export const generateMessagePreview = (template, userName = 'John') => {
    if (!template) return '';
    
    return template.replace(/\{\{name\}\}/g, userName);
};

/**
 * Calculate word count for a message
 * 
 * @param {string} message - Message to count words
 * @returns {number} Word count
 */
export const getWordCount = (message) => {
    if (!message) return 0;
    return message.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Seed zodiac messages with initial data
 * 
 * @returns {Promise<Object>} API response with seeding result
 */
export const seedZodiacMessagesApi = async () => {
    try {
        const response = await axios.post('/admin/zodiac-message/seed');
        return response.data;
    } catch (error) {
        console.error('Error seeding zodiac messages:', error);
        throw error;
    }
};

/**
 * Get zodiac message statistics
 * 
 * @returns {Promise<Object>} API response with statistics
 */
export const getZodiacMessageStatsApi = async () => {
    try {
        const response = await axios.get('/admin/zodiac-message/stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching zodiac message stats:', error);
        throw error;
    }
};

